import { useReducer, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { KiiDialogLayout } from "../kii";
import { useCreateAgentKey, useCreateAgentMember } from "../../hooks/useWorkspaceMembers";
import { AGENT_ROLES, addAgentReducer, canConfirmAgent, initialAddAgent, runAddAgent } from "../../lib/agentMembers";
import type { AgentRole } from "../../services/naraDoor";
import { AgentConsent } from "./AgentConsent";
import { AgentKeyReveal } from "./AgentKeyReveal";

interface AddAgentDialogProps {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const inputClass = "w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";

/** Name and role, then the consent (D5), then the key shown once. Closing drops the key. */
export function AddAgentDialog({ workspaceId, open, onOpenChange }: AddAgentDialogProps) {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(addAgentReducer, initialAddAgent);
  const createMember = useCreateAgentMember(workspaceId);
  const createKey = useCreateAgentKey(workspaceId);

  const close = () => {
    dispatch({ type: "reset" });
    createKey.reset();
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (!canConfirmAgent(state)) return;
    const action = await runAddAgent(createMember.mutateAsync, createKey.mutateAsync, workspaceId, state.name, state.role);
    // The key lives in the dialog state only; clear the mutation's copy.
    createKey.reset();
    dispatch(action);
  };

  const handleContinue = (e: FormEvent) => {
    e.preventDefault();
    dispatch({ type: "continue" });
  };

  return (
    <KiiDialogLayout open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())} title={t("agent.add.title")} description={t("agent.add.description")} size="md">
      {state.step === "form" && (
        <form onSubmit={handleContinue} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">{t("agent.add.name")}</label>
            <input type="text" value={state.name} maxLength={80} onChange={(e) => dispatch({ type: "setName", name: e.target.value })} placeholder={t("agent.add.namePlaceholder")} className={inputClass} autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">{t("agent.add.role")}</label>
            <select value={state.role} onChange={(e) => dispatch({ type: "setRole", role: e.target.value as AgentRole })} className={inputClass}>
              {AGENT_ROLES.map((r) => (
                <option key={r} value={r}>{t(`workspaceMembers.roles.${r}`)}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">{t(`agent.add.roleHint.${state.role}`)}</p>
          </div>
          {state.error === "nameRequired" && (
            <div role="alert" className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">{t("agent.add.nameRequired")}</div>
          )}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={close} className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors">{t("common.cancel")}</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">{t("agent.add.continue")}</button>
          </div>
        </form>
      )}

      {state.step === "consent" && (
        <AgentConsent
          name={state.name}
          role={state.role}
          accepted={state.accepted}
          pending={createMember.isPending || createKey.isPending}
          error={state.error === "createFailed" ? t("agent.add.createFailed") : null}
          onAcceptedChange={(accepted) => dispatch({ type: "setAccepted", accepted })}
          onConfirm={handleConfirm}
          onBack={() => dispatch({ type: "back" })}
        />
      )}

      {state.step === "key" && state.key && <AgentKeyReveal keyValue={state.key} onDone={close} />}

      {state.step === "keyFailed" && (
        <div className="mt-4 space-y-4">
          <div role="alert" className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">{t("agent.add.keyFailed")}</div>
          <div className="flex justify-end">
            <button type="button" onClick={close} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">{t("agent.key.done")}</button>
          </div>
        </div>
      )}
    </KiiDialogLayout>
  );
}
